import { fetchEventSource } from '@microsoft/fetch-event-source';

import { customFetch } from '@/lib/utils';

import type {
  ConnectionData,
  ConnectionStreamEvent,
  DataConnection,
  DataConnector,
  ListConnectionDataOptions,
  PublicKey,
  StaticIpResponse
} from './types';

export const getConnectors = async ({
  accessToken
}: {
  accessToken: string;
}): Promise<DataConnector[]> => {
  const res = await customFetch('/backend/data-connectors/types', {
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    method: 'GET'
  });

  if (!res.ok) {
    throw new Error('Failed to fetch data connectors');
  }

  return (await res.json()) as DataConnector[];
};

export const getConnector = async ({ accessToken, connectorId }): Promise<DataConnector> => {
  const res = await customFetch(`/backend/data-connectors/types/${connectorId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!res.ok) {
    throw new Error('Failed to fetch data connector');
  }

  return (await res.json()) as DataConnector;
};

export const getUserConnections = async ({ accessToken }): Promise<DataConnection[]> => {
  const res = await customFetch('/backend/data-connectors', {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!res.ok) {
    throw new Error('Failed to fetch user connections');
  }

  return (await res.json()) as DataConnection[];
};

/*
Convert a string into an ArrayBuffer
from https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
*/
function str2ab(str: string): ArrayBuffer {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
}

async function encryptConnectionInfo(connectionInfo: any, publicKey: PublicKey) {
  const pemHeader = '-----BEGIN PUBLIC KEY-----';
  const pemFooter = '-----END PUBLIC KEY-----';
  const key = publicKey.public_key;
  const pemContents = key.replace(pemHeader, '').replace(pemFooter, '').replace(/\s/g, '');

  try {
    const binaryDerString = window.atob(pemContents);
    const binaryDer = str2ab(binaryDerString);

    const cryptoKey = await window.crypto.subtle.importKey(
      'spki',
      binaryDer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256'
      },
      true,
      ['encrypt', 'wrapKey']
    );

    const enc = new TextEncoder();
    const msg = enc.encode(JSON.stringify(connectionInfo));

    const aeskey = await window.crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256
      },
      true,
      ['encrypt', 'wrapKey']
    );

    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    const encryptedData = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv
      },
      aeskey,
      msg
    );

    const wrappedAesKey = await window.crypto.subtle.wrapKey('raw', aeskey, cryptoKey, 'RSA-OAEP');

    const textb64 = window.btoa(String.fromCharCode(...new Uint8Array(encryptedData)));
    const encryptedConnectionInfo = {
      encrypted_data: textb64,
      encrypted_key: window.btoa(String.fromCharCode(...new Uint8Array(wrappedAesKey))),
      iv: window.btoa(String.fromCharCode(...new Uint8Array(iv)))
    };
    return encryptedConnectionInfo;
  } catch {
    throw new Error('Failed to encrypt connection info');
  }
}

export const createConnection = async ({
  accessToken,
  connectorId,
  connectionInfo,
  connectionName,
  publicKey
}): Promise<DataConnection> => {
  try {
    const encryptedConnectionInfo = await encryptConnectionInfo(connectionInfo, publicKey);

    const requestBody = {
      connector_id: connectorId,
      connection_info: encryptedConnectionInfo,
      name: connectionName
    };

    const res = await customFetch('/backend/data-connectors', {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      method: 'POST',
      body: JSON.stringify(requestBody)
    });

    if (!res.ok) {
      let errorData;
      try {
        errorData = await res.json();
      } catch {
        throw new Error('Failed to create connection');
      }
      throw new Error(errorData?.message || errorData?.error || 'Failed to create connection');
    }

    return (await res.json()) as DataConnection;
  } catch (error) {
    console.error('Error creating connection:', error);
    throw error;
  }
};

export const streamingEnableConnection = async ({
  accessToken,
  connectionId,
  conversationId,
  signal,
  onMessage,
  waitForSchema,
  attachToConversation = false
}: {
  accessToken: string;
  connectionId: string;
  conversationId: string;
  signal?: AbortSignal;
  onMessage?: (event: ConnectionStreamEvent) => void;
  waitForSchema: boolean;
  attachToConversation?: boolean;
}): Promise<void> => {
  let attachmentCreated = false;

  if (attachToConversation) {
    const attachment = await attachConnectionToConversation({
      accessToken,
      connectionId,
      conversationId
    });
    attachmentCreated = attachment.created;
  }

  try {
    await fetchEventSource(
      `/backend/data-connectors/${connectionId}/streaming-enable`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ wait_for_schema: waitForSchema }),
        signal,
        onmessage(ev) {
          const payload = JSON.parse(ev.data) as ConnectionStreamEvent;

          onMessage?.(payload);

          if (payload?.type === 'connection' && payload.status === 'error') {
            throw new Error(
              payload.message?.trim() || payload.raw_message?.trim() || 'Failed to enable connector'
            );
          }
        },
        onerror(err) {
          throw err;
        }
      }
    );
  } catch (error) {
    if (attachmentCreated) {
      await disableConnection({ accessToken, connectionId, conversationId }).catch(() => undefined);
    }
    throw error;
  }
};

export const getConnectionsForConversation = async ({
  accessToken,
  conversationId
}: {
  accessToken: string;
  conversationId: string;
}): Promise<DataConnection[]> => {
  const res = await customFetch(`/backend/conversations/${conversationId}/data-connectors`, {
    headers: { Authorization: `Bearer ${accessToken}` }
  });

  if (!res.ok) throw new Error('Failed to fetch conversation connections');
  return (await res.json()) as DataConnection[];
};

export const attachConnectionToConversation = async ({
  accessToken,
  connectionId,
  conversationId
}: {
  accessToken: string;
  connectionId: string;
  conversationId: string;
}): Promise<{ connection: DataConnection; created: boolean }> => {
  const res = await customFetch(`/backend/conversations/${conversationId}/data-connectors`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ connection_id: connectionId })
  });

  if (!res.ok) throw new Error('Failed to attach connection');
  return (await res.json()) as { connection: DataConnection; created: boolean };
};

export const getPublicKey = async ({ accessToken }): Promise<PublicKey> => {
  const res = await customFetch('/backend/data-connectors/public-key', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!res.ok) {
    throw new Error('Failed to get public key');
  }

  return (await res.json()) as PublicKey;
};

export const deleteConnection = async ({
  accessToken,
  connectionId
}: {
  accessToken: string;
  connectionId: string;
}): Promise<void> => {
  const res = await customFetch(`/backend/data-connectors/${connectionId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    method: 'DELETE'
  });

  if (!res.ok) {
    throw new Error('Failed to delete connection');
  }
};

export const disableConnection = async ({
  accessToken,
  connectionId,
  conversationId
}): Promise<boolean> => {
  const res = await customFetch(
    `/backend/conversations/${conversationId}/data-connectors/${connectionId}`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      method: 'DELETE'
    }
  );

  if (!res.ok) {
    throw new Error('Failed to disable connection');
  }

  return true;
};

export const updateConnection = async ({
  accessToken,
  connectionId,
  connectionName,
  connectionInfo,
  publicKey,
  abortSignal = null
}): Promise<DataConnection> => {
  const encryptedConnectionInfo = await encryptConnectionInfo(connectionInfo, publicKey);

  const res = await customFetch(`/backend/data-connectors/${connectionId}`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    method: 'PATCH',
    body: JSON.stringify({ name: connectionName, connection_info: encryptedConnectionInfo }),
    signal: abortSignal
  });

  if (!res.ok) {
    let errorData;
    try {
      errorData = await res.json();
    } catch {
      throw new Error('Failed to update connection');
    }
    throw new Error(errorData?.message || errorData?.error || 'Failed to update connection');
  }

  return (await res.json()) as DataConnection;
};

export const updateConnectionName = async ({
  accessToken,
  connectionId,
  connectionName
}: {
  accessToken: string;
  connectionId: string;
  connectionName: string;
}): Promise<DataConnection> => {
  const res = await customFetch(`/backend/data-connectors/${connectionId}`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    method: 'PATCH',
    body: JSON.stringify({ name: connectionName })
  });

  if (!res.ok) {
    throw new Error('Failed to update connection');
  }

  return (await res.json()) as DataConnection;
};

export const updateConnectionCustomInstructions = async ({
  accessToken,
  connectionId,
  customInstructions
}: {
  accessToken: string;
  connectionId: string;
  customInstructions: string;
}): Promise<void> => {
  const res = await customFetch(
    `/backend/data-connectors/${connectionId}/custom-instructions`,
    {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      method: 'PUT',
      body: JSON.stringify({ custom_instructions: customInstructions })
    }
  );

  if (!res.ok) {
    throw new Error('Failed to update connection custom instructions');
  }
};

export const getStaticIp = async (): Promise<StaticIpResponse> => {
  const response = await customFetch('/backend/data-connectors/static-ip', {
    cache: 'no-store'
  });

  if (!response.ok) {
    throw new Error('Failed to get static IP');
  }

  return (await response.json()) as StaticIpResponse;
};

export type SchemaFilterRow = {
  level: 'schema' | 'table' | 'column';
  schema_name: string | null;
  table_name: string | null;
  column_name: string | null;
  filtered_out: boolean;
};

export type ConnectionSchemaRow = {
  level: 'schema' | 'table' | 'column';
  schema_name: string | null;
  table_name: string | null;
  column_name: string | null;
  data_type: string | null;
};

export type BrowseSchemaLearning = {
  id: string;
  text: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  review_status: string | null;
  approved_by_email: string | null;
};

export type BrowseSchemaRow = {
  id: string;
  level: 'database' | 'schema' | 'table' | 'column';
  database_name: string | null;
  schema_name: string | null;
  table_name: string | null;
  column_name: string | null;
  data_type: string | null;
  indexed_columns: string | null;
  foreign_key_refs: string | null;
  annotations: string | null;
  filtered_out: boolean;
  is_frozen: boolean;
  last_seen_at: string | null;
  learnings: BrowseSchemaLearning[];
  approved_learnings: BrowseSchemaLearning[];
  rejected_learnings: BrowseSchemaLearning[];
};

export const getConnectionSchema = async <IncludeLearnings extends boolean = false>({
  accessToken,
  connectionId,
  includeLearnings
}: {
  accessToken: string;
  connectionId: string;
  includeLearnings?: IncludeLearnings;
}): Promise<{
  schema: IncludeLearnings extends true ? BrowseSchemaRow[] : SchemaFilterRow[];
}> => {
  const params = new URLSearchParams();
  if (includeLearnings === true) params.set('include', 'learnings');
  const query = params.size ? `?${params.toString()}` : '';
  const res = await customFetch(`/backend/data-connectors/${connectionId}/schema${query}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!res.ok) {
    const errorData: { message?: string } | undefined = await res.json().catch(() => undefined);
    throw new Error(errorData?.message || 'Failed to fetch connection schema');
  }

  return (await res.json()) as {
    schema: IncludeLearnings extends true ? BrowseSchemaRow[] : SchemaFilterRow[];
  };
};

export const freezeLearning = async ({
  accessToken,
  connectionId,
  setFreeze
}: {
  accessToken: string;
  connectionId: string;
  setFreeze: boolean;
}): Promise<void> => {
  const res = await customFetch(`/backend/data-connectors/${connectionId}/learnings/settings`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ freeze: setFreeze })
  });

  if (!res.ok) {
    throw new Error('Failed to update learning freeze state');
  }
};

export const updateLearning = async ({
  accessToken,
  connectionId,
  learningData
}: {
  accessToken: string;
  connectionId: string;
  learningData: Record<string, string>;
}): Promise<void> => {
  const res = await customFetch(`/backend/data-connectors/${connectionId}/learnings`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      learning_data: learningData
    })
  });

  if (!res.ok) {
    throw new Error('Failed to update learning data');
  }
};

export const getLearning = async ({
  accessToken,
  connectionId
}: {
  accessToken: string;
  connectionId: string;
}): Promise<{ learning_data: { learning: Record<string, string>; freeze: boolean } }> => {
  const res = await customFetch(`/backend/data-connectors/${connectionId}/learnings`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!res.ok) {
    throw new Error('Failed to fetch learning data');
  }

  return (await res.json()) as {
    learning_data: { learning: Record<string, string>; freeze: boolean };
  };
};

export const restoreLearning = async ({
  accessToken,
  learningId
}: {
  accessToken: string;
  learningId: string;
}): Promise<void> => {
  const res = await customFetch(`/backend/data-connectors/learnings/${learningId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ is_active: true })
  });

  if (!res.ok) {
    throw new Error('Failed to restore learning');
  }
};

export const deleteLearning = async ({
  accessToken,
  learningId
}: {
  accessToken: string;
  learningId: string;
}): Promise<void> => {
  const res = await customFetch(`/backend/data-connectors/learnings/${learningId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!res.ok) {
    throw new Error('Failed to delete learning');
  }
};

export const listConnectionData = async ({
  accessToken,
  connectionId,
  conversationId,
  params
}: ListConnectionDataOptions) => {
  const searchParams = new URLSearchParams({
    conversationId,
    type: params.type
  });
  if ('dataset_name' in params) searchParams.set('datasetName', params.dataset_name);
  if ('table_name' in params) searchParams.set('tableName', params.table_name);
  if ('limit' in params && params.limit !== undefined) {
    searchParams.set('limit', String(params.limit));
  }
  if ('offset' in params && params.offset !== undefined) {
    searchParams.set('offset', String(params.offset));
  }

  const res = await customFetch(
    `/backend/data-connectors/${connectionId}/data?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }
  );

  if (!res.ok) throw await res.json();

  return res.json() as Promise<ConnectionData[]>;
};

export const toggleSchemaVisibility = async ({
  accessToken,
  connectionId,
  schemaId,
  hidden
}: {
  accessToken: string;
  connectionId: string;
  schemaId: string;
  hidden: boolean;
}): Promise<{ success: boolean; hidden: boolean }> => {
  const res = await customFetch(
    `/backend/data-connectors/${connectionId}/schema/${schemaId}/visibility`,
    {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ hidden })
    }
  );

  if (!res.ok) {
    throw new Error('Failed to toggle visibility');
  }

  return (await res.json()) as { success: boolean; hidden: boolean };
};

export const batchToggleSchemaVisibility = async ({
  accessToken,
  connectionId,
  schemaIds,
  hidden
}: {
  accessToken: string;
  connectionId: string;
  schemaIds: string[];
  hidden: boolean;
}): Promise<{ success: boolean; updated: number }> => {
  const res = await customFetch(`/backend/data-connectors/${connectionId}/schema/visibility`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ schema_ids: schemaIds, hidden })
  });

  if (!res.ok) {
    throw new Error('Failed to batch toggle visibility');
  }

  return (await res.json()) as { success: boolean; updated: number };
};

export const toggleSchemaFrozen = async ({
  accessToken,
  connectionId,
  schemaId,
  frozen
}: {
  accessToken: string;
  connectionId: string;
  schemaId: string;
  frozen: boolean;
}): Promise<{ success: boolean; frozen: boolean }> => {
  const res = await customFetch(
    `/backend/data-connectors/${connectionId}/schema/${schemaId}/frozen`,
    {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ frozen })
    }
  );

  if (!res.ok) {
    throw new Error('Failed to toggle frozen state');
  }

  return (await res.json()) as { success: boolean; frozen: boolean };
};

export const createLearning = async ({
  accessToken,
  schemaId,
  connectionId,
  text
}: {
  accessToken: string;
  schemaId: string;
  connectionId: string;
  text: string;
}): Promise<{ success: boolean; learning_id: string }> => {
  const res = await customFetch(`/backend/data-connectors/${connectionId}/learnings`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ schema_id: schemaId, text })
  });

  if (!res.ok) {
    throw new Error('Failed to create learning');
  }

  return (await res.json()) as { success: boolean; learning_id: string };
};

export interface ReviewLearning {
  id: string;
  text: string; // Current/original text
  suggested_text: string | null; // For updates, the proposed new text
  original_text: string | null; // For updates, the original text (from root)
  root_id: string | null; // For updates/deletes, points to original
  operation: string | null; // 'create', 'update', 'delete'
  review_status: string | null; // 'pending', 'approved', 'rejected', null
  created_at: string;
  updated_at: string;
  is_active: boolean;
  approved: boolean | null; // Computed: null = pending, true = approved, false = rejected
  suggestion_type: string | null; // Computed from operation
  dc_schema_id: string;
  schema_name: string;
  table_name: string | null;
  column_name: string | null;
  level: 'schema' | 'table' | 'column';
}

export const getReviewLearnings = async ({
  accessToken,
  connectionId
}: {
  accessToken: string;
  connectionId: string;
}): Promise<{ learnings: ReviewLearning[] }> => {
  const res = await customFetch(
    `/backend/data-connectors/${connectionId}/learnings?review_status=pending`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }
  );

  if (!res.ok) {
    throw new Error('Failed to fetch review learnings');
  }

  return (await res.json()) as { learnings: ReviewLearning[] };
};

export const approveLearning = async ({
  accessToken,
  learningId
}: {
  accessToken: string;
  learningId: string;
}): Promise<{ success: boolean }> => {
  const res = await customFetch(`/backend/data-connectors/learning-reviews/${learningId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ review_status: 'approved' })
  });

  if (!res.ok) {
    throw new Error('Failed to approve learning');
  }

  return (await res.json()) as { success: boolean };
};

export const rejectLearning = async ({
  accessToken,
  learningId
}: {
  accessToken: string;
  learningId: string;
}): Promise<{ success: boolean }> => {
  const res = await customFetch(`/backend/data-connectors/learning-reviews/${learningId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ review_status: 'rejected' })
  });

  if (!res.ok) {
    throw new Error('Failed to reject learning');
  }

  return (await res.json()) as { success: boolean };
};

export const revertLearningToPending = async ({
  accessToken,
  learningId
}: {
  accessToken: string;
  learningId: string;
}): Promise<{ success: boolean }> => {
  const res = await customFetch(`/backend/data-connectors/learning-reviews/${learningId}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ review_status: 'pending' })
  });

  if (!res.ok) {
    throw new Error('Failed to revert learning');
  }

  return (await res.json()) as { success: boolean };
};

export const batchApproveLearnings = async ({
  accessToken,
  learningIds
}: {
  accessToken: string;
  learningIds: string[];
}): Promise<{ success: boolean; count: number }> => {
  const res = await customFetch('/backend/data-connectors/learning-reviews', {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ learning_ids: learningIds, review_status: 'approved' })
  });

  if (!res.ok) {
    throw new Error('Failed to batch approve learnings');
  }

  return (await res.json()) as { success: boolean; count: number };
};

export const batchRejectLearnings = async ({
  accessToken,
  learningIds
}: {
  accessToken: string;
  learningIds: string[];
}): Promise<{ success: boolean; count: number }> => {
  const res = await customFetch('/backend/data-connectors/learning-reviews', {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ learning_ids: learningIds, review_status: 'rejected' })
  });

  if (!res.ok) {
    throw new Error('Failed to batch reject learnings');
  }

  return (await res.json()) as { success: boolean; count: number };
};

export const batchRevertLearnings = async ({
  accessToken,
  learningIds
}: {
  accessToken: string;
  learningIds: string[];
}): Promise<{ success: boolean; count: number }> => {
  const res = await customFetch('/backend/data-connectors/learning-reviews', {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ learning_ids: learningIds, review_status: 'pending' })
  });

  if (!res.ok) {
    throw new Error('Failed to batch revert learnings');
  }

  return (await res.json()) as { success: boolean; count: number };
};

export interface ReviewedLearning {
  id: string;
  text: string;
  suggested_text: string | null;
  original_text: string | null;
  root_id: string | null;
  operation: string | null;
  review_status: 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
  reviewed_at: string | null;
  is_active: boolean;
  suggestion_type: string | null;
  dc_schema_id: string;
  schema_name: string;
  table_name: string | null;
  column_name: string | null;
  level: 'schema' | 'table' | 'column';
  approved_by_email: string | null;
}

export const getReviewedLearnings = async ({
  accessToken,
  connectionId
}: {
  accessToken: string;
  connectionId: string;
}): Promise<{ approved: ReviewedLearning[]; rejected: ReviewedLearning[] }> => {
  const res = await customFetch(
    `/backend/data-connectors/${connectionId}/learnings?review_status=reviewed`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }
  );

  if (!res.ok) {
    throw new Error('Failed to fetch reviewed learnings');
  }

  return (await res.json()) as { approved: ReviewedLearning[]; rejected: ReviewedLearning[] };
};

export const getAutoapprovalMode = async ({
  accessToken,
  connectionId
}: {
  accessToken: string;
  connectionId: string;
}): Promise<{ autoapproval_enabled: boolean }> => {
  const res = await customFetch(
    `/backend/data-connectors/${connectionId}/autoapproval`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }
  );

  if (!res.ok) {
    throw new Error('Failed to get autoapproval mode');
  }

  return (await res.json()) as { autoapproval_enabled: boolean };
};

export const setAutoapprovalMode = async ({
  accessToken,
  connectionId,
  autoapprovalEnabled
}: {
  accessToken: string;
  connectionId: string;
  autoapprovalEnabled: boolean;
}): Promise<{ success: boolean; autoapproval_enabled: boolean }> => {
  const res = await customFetch(
    `/backend/data-connectors/${connectionId}/autoapproval`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ autoapproval_enabled: autoapprovalEnabled })
    }
  );

  if (!res.ok) {
    throw new Error('Failed to set autoapproval mode');
  }

  return (await res.json()) as { success: boolean; autoapproval_enabled: boolean };
};
